import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    UserCheck,
    Clock,
    Search,
    Filter,
    Users,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Download,
    Printer,
    RefreshCw,
    Eye,
    Bell,
    MapPin,
    BookOpen,
    Award,
    Edit,
    Save,
    Plus,
    Building,
    X
} from 'lucide-react';
import { attendanceApi, classesApi, campusApi, dashboardApi, api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { SessionSlotCard, SessionStaffTimes } from '../components/attendance/SessionStaffSlots';
import { HomeworkSheet } from '../components/attendance/HomeworkSheet';
import { AssessmentSheet } from '../components/attendance/AssessmentSheet';
import { RollCallTable, type RollCallDetail, type RollCallStatus } from '../components/attendance/RollCallTable';
import type { GradeSheetStudent, MakeupStudentCandidate, SessionRegistrationContext } from '../types';
import {
    Button,
    DataToolbar,
    Input,
    EmptyState,
    LoadingState,
    LocalPagination,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    StatCard,
    StatusBadge,
    useLocalPagination,
    useToast,
    DateInput,
    SearchSelect,
} from '../components/ui';
import { classPickerOptions, personPickerOptions } from '../utils/pickerOptions';
import { matchesQuery } from '../utils/search';
import { PageHeader } from '../components/PageHeader';
import { openOnRowClick } from '../utils/rowClick';
import { centerDateKey } from '../utils/geolocation';

type ConfirmationDialogState = {
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel?: string;
    tone?: 'warning' | 'danger';
    onConfirm: () => Promise<void> | void;
};

type SessionTab = 'attendance' | 'homework' | 'tests';

const SESSION_TABS: Array<{ value: SessionTab; label: string; short: string }> = [
    { value: 'attendance', label: 'Điểm danh', short: 'Điểm danh' },
    { value: 'homework', label: 'Bài tập về nhà', short: 'BTVN' },
    { value: 'tests', label: 'Điểm kiểm tra', short: 'Kiểm tra' },
];

type AttendanceAlertModal = 'all' | 'missing_attendance' | 'no_student_attendance' | 'student_status';

const attendanceAlertPageSizeOptions = [5, 10, 15, 20, 50] as const;
const ATTENDANCE_PAGE_SIZE = 5;
const ATTENDANCE_ALERT_LIST_PAGE_SIZE = 5;

const attendanceAlertModalLabels: Record<AttendanceAlertModal, string> = {
    all: 'Tất cả cảnh báo',
    missing_attendance: 'Lớp chưa tạo điểm danh',
    no_student_attendance: 'Lớp chưa điểm danh học sinh',
    student_status: 'Học sinh vắng / muộn / chưa điểm danh',
};

// Helper function to parse date string correctly (avoiding timezone issues)
const parseLocalDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
};

// Helper function to format date as YYYY-MM-DD
const formatDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const Attendance = () => {
    const { user } = useAuth();
    const { showToast: showUiToast } = useToast();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryModalOpenedRef = React.useRef(false);

    // Helper function to check if user can modify teacher/assistant assignments
    const canModifyAttendanceAssignments = () => {
        return user && (user.role === 'ADMIN' || user.role === 'MANAGER');
    };

    // Helper function to check if user can edit attendance data
    const canEditAttendanceData = () => {
        if (!user || !selectedClassDetail || !attendanceSession) return false;

        // ADMIN and MANAGER can always edit
        if (user.role === 'ADMIN' || user.role === 'MANAGER') {
            return true;
        }

        // TEACHER and ASSISTANT can edit if they are assigned to this specific attendance session
        if (user.role === 'TEACHER' || user.role === 'ASSISTANT') {
            const isSessionTeacher = attendanceSession.teacherId === user.id;
            const isSessionAssistant = attendanceSession.assistantId === user.id;
            return isSessionTeacher || isSessionAssistant;
        }

        return false;
    };
    // Hôm nay theo giờ Việt Nam; dùng giờ UTC thì từ 0h đến 7h sáng sẽ ra ngày hôm qua.
    const [selectedDate, setSelectedDate] = useState(() => centerDateKey());
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const [selectedClass, setSelectedClass] = useState('all');
    const [selectedCampus, setSelectedCampus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAlerts, setShowAlerts] = useState(true);
    const [loading, setLoading] = useState(true);
    const [attendanceData, setAttendanceData] = useState<any[]>([]);
    const [stats, setStats] = useState<any>({});
    const [classOptions, setClassOptions] = useState<any[]>([]);
    // Lớp cho bộ lọc: chỉ lớp có lịch ngày đang chọn, thuộc cơ sở đang chọn và người dùng được xem.
    const [dayClassOptions, setDayClassOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [campusOptions, setCampusOptions] = useState<any[]>([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedClassDetail, setSelectedClassDetail] = useState<any>(null);
    const [classStudents, setClassStudents] = useState<any[]>([]);
    const [markingAttendance, setMarkingAttendance] = useState(false);
    const [loadingDetailClassId, setLoadingDetailClassId] = useState<string | null>(null);
    const [hasUnsavedDetailChanges, setHasUnsavedDetailChanges] = useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogState | null>(null);
    const [isConfirmingDialog, setIsConfirmingDialog] = useState(false);
    const [isSavingAll, setIsSavingAll] = useState(false);

    // Tab trong modal buổi học; tab đã mở được giữ lại để không mất số đang nhập.
    const [detailTab, setDetailTab] = useState<SessionTab>('attendance');
    const [visitedTabs, setVisitedTabs] = useState<SessionTab[]>(['attendance']);
    // Tăng sau mỗi lần lưu điểm danh để tab BTVN và Kiểm tra tải lại danh sách học sinh.
    const [gradesVersion, setGradesVersion] = useState(0);
    const openDetailTab = (tab: SessionTab) => {
        setDetailTab(tab);
        setVisitedTabs(current => (current.includes(tab) ? current : [...current, tab]));
    };

    // New fields for attendance tracking
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedAssistant, setSelectedAssistant] = useState('');
    const [attendanceNotes, setAttendanceNotes] = useState('');
    const [teachersAndAssistants, setTeachersAndAssistants] = useState<any[]>([]);
    const assignmentUserOptions = useMemo(() => personPickerOptions(teachersAndAssistants), [teachersAndAssistants]);
    const [attendanceSession, setAttendanceSession] = useState<any>(null);
    const [sessionJustCreated, setSessionJustCreated] = useState(false);
    const [sessionRegistration, setSessionRegistration] = useState<SessionRegistrationContext | null>(null);
    const [detailClassCampusId, setDetailClassCampusId] = useState('');

    // Missing attendance alerts
    const [missingAttendanceAlerts, setMissingAttendanceAlerts] = useState<any[]>([]);
    const [noStudentAttendanceAlerts, setNoStudentAttendanceAlerts] = useState<any[]>([]);
    const [isCheckingAlerts, setIsCheckingAlerts] = useState(false);
    const [attendanceAlertModal, setAttendanceAlertModal] = useState<AttendanceAlertModal | null>(null);
    const [attendanceAlertModalPage, setAttendanceAlertModalPage] = useState(1);
    const [attendanceAlertPageSize, setAttendanceAlertPageSize] = useState(5);
    const [creatingManualAlertKey, setCreatingManualAlertKey] = useState<string | null>(null);

    // Add Student feature
    const [showAddStudentModal, setShowAddStudentModal] = useState(false);
    const [studentSearchTerm, setStudentSearchTerm] = useState('');
    // Make-up students come from other classes, which teachers usually can't list, so the session searches for them.
    const [makeupResults, setMakeupResults] = useState<MakeupStudentCandidate[]>([]);
    const [searchingMakeup, setSearchingMakeup] = useState(false);
    const makeupQuery = studentSearchTerm.trim();
    const makeupQueryTooShort = makeupQuery.length < 2;

    useEffect(() => {
        if (!showAddStudentModal || !selectedClassDetail || makeupQueryTooShort) {
            setMakeupResults([]);
            setSearchingMakeup(false);
            return undefined;
        }
        let active = true;
        setSearchingMakeup(true);
        const timer = window.setTimeout(() => {
            attendanceApi.searchMakeupStudents(selectedClassDetail.classId, selectedDate, makeupQuery)
                .then(response => active && setMakeupResults(response.data.data ?? []))
                .catch((error: any) => active && showToast(error?.message || 'Không tìm được học sinh.', 'error'))
                .finally(() => active && setSearchingMakeup(false));
        }, 300);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
        // showToast is redefined each render; the search only reruns when what it looks for changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAddStudentModal, selectedClassDetail, selectedDate, makeupQuery, makeupQueryTooShort]);

    const closeAddStudentModal = () => {
        setShowAddStudentModal(false);
        setStudentSearchTerm('');
    };

    // Add student to current attendance session
    const handleAddStudent = async (student: any) => {
        // Check if student is already in the current session
        const isAlreadyAdded = classStudents.some(s => s.id === student.id);
        if (isAlreadyAdded) {
            showToast('⚠️ Học sinh này đã có trong danh sách!', 'info');
            return;
        }

        // Add student to the current session list
        const newStudent = {
            id: student.id,
            name: `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.name,
            phone: student.phone || '',
            email: student.email || '',
            // Học sinh đến học bù thì đang có mặt; đổi được như mọi học sinh khác.
            status: 'present',
            checkedAt: new Date(),
            lateArrivalTime: null,
            lateNote: '',
            absentNote: '',
            excusedNote: '',
            note: '',
            isTemporary: true, // Mark as temporary addition
            homework: {
                assignedExercises: 0.0,
                completedExercises: 0.0,
                correctExercises: 0.0,
                note: ''
            }
        };

        setClassStudents(prev => [...prev, newStudent]);
        setHasUnsavedDetailChanges(true);
        setShowAddStudentModal(false);
        setStudentSearchTerm('');
        showToast('Đã thêm vào danh sách. Bấm "Lưu điểm danh" để lưu.', 'success');
    };

    const commitSelectedDate = (dateString: string) => {
        setSelectedDate(dateString);
        setSelectedClass('all');
    };

    // Reload whenever the filters change, once the option lists have been fetched.
    // Teachers can get an empty class list, so wait for the fetch to finish rather than for non-empty lists.
    useEffect(() => {
        if (optionsLoaded) {
            loadAttendanceData();
        }
    }, [selectedDate, selectedClass, selectedCampus, optionsLoaded]);

    useEffect(() => {
        Promise.all([
            loadClassOptions(),
            loadCampusOptions(),
            loadTeachersAndAssistants()
        ]).catch((error) => {
            console.error('❌ Error loading options:', error);
        }).finally(() => {
            setOptionsLoaded(true);
        });
    }, []);

    // Follow ?date= when the link changes, without pinning the picker to it afterwards.
    useEffect(() => {
        const queryDate = searchParams.get('date');
        if (queryDate) {
            commitSelectedDate(queryDate);
            queryModalOpenedRef.current = false;
        }
    }, [searchParams]);

    // Final fallback check when all data is ready
    useEffect(() => {
        // Only check when we have all required data and not currently loading
        if (classOptions.length > 0 && campusOptions.length > 0 && !loading && attendanceData.length >= 0) {
            // console.log('🎯 Final check - all data ready, checking missing attendance alerts...');
            // console.log('  - classOptions:', classOptions.length);
            // console.log('  - campusOptions:', campusOptions.length);
            // console.log('  - attendanceData:', attendanceData.length);
            // console.log('  - loading:', loading);
            setTimeout(() => checkMissingAttendanceAlerts(), 200);
        }
    }, [classOptions.length, campusOptions.length, loading, attendanceData.length]);

    useEffect(() => {
        const queryClassId = searchParams.get('classId');
        if (!queryClassId || queryModalOpenedRef.current || loading || classOptions.length === 0) {
            return;
        }

        const attendanceItem = attendanceData.find(item => String(item.classId) === String(queryClassId));
        const classItem = classOptions.find(item => String(item.id) === String(queryClassId));
        const target = attendanceItem || (classItem ? {
            classId: classItem.id,
            className: classItem.name,
            subject: typeof classItem.subject === 'object' ? classItem.subject?.name : classItem.subject,
            campus: typeof classItem.campus === 'object' ? classItem.campus?.name : classItem.campus,
            room: classItem.room,
            teacher: classItem.teacher || classItem.teacherInfo?.name || 'Chưa phân công',
            timeSlot: '',
            date: parseLocalDate(selectedDate),
            status: 'not_started',
            totalStudents: 0,
            presentCount: 0,
            absentCount: 0,
            lateCount: 0,
            excusedCount: 0,
            students: [],
        } : null);

        if (!target) {
            return;
        }

        queryModalOpenedRef.current = true;
        void handleViewDetail(target);
    }, [attendanceData, classOptions, loading, searchParams, selectedDate]);

    // silent: tải lại ngầm (khi đóng modal) mà không thay danh sách bằng vòng xoay, để trang không nháy.
    const loadAttendanceData = async ({ silent = false }: { silent?: boolean } = {}) => {
        try {
            // console.log('=== LOADING ATTENDANCE DATA ===');
            // console.log('Options loaded - Classes:', classOptions.length, 'Campuses:', campusOptions.length);
            if (!silent) setLoading(true);

            // Load existing attendance data for the selected date
            const attendanceResponse = await attendanceApi.getAll({
                date: selectedDate,
                classId: selectedClass !== 'all' ? selectedClass : undefined,
                campusId: selectedCampus !== 'all' ? selectedCampus : undefined
            });

            const existingAttendance = attendanceResponse.data.data || [];

            // Transform the data to match the expected format
            const attendanceData = existingAttendance.map((item: any) => ({
                id: item.id,
                classId: item.classId,
                className: item.className,
                subject: item.subject,
                campus: item.campus,
                room: item.room,
                teacher: item.teacher,
                assistant: item.assistant,
                timeSlot: item.timeSlot,
                date: new Date(item.date),
                status: item.status,
                totalStudents: item.totalStudents,
                makeupCount: item.makeupCount || 0,
                presentCount: item.presentCount,
                absentCount: item.absentCount,
                lateCount: item.lateCount,
                excusedCount: item.excusedCount,
                unmarkedStudents: item.unmarkedStudents || [],
                students: item.students || []
            }));

            // console.log('Raw attendance response:', existingAttendance);
            // console.log('Selected date for attendance query:', selectedDate);
            // console.log('Transformed attendance data:', attendanceData.map(item => ({
            //     classId: item.classId,
            //     className: item.className,
            //     originalDate: item.date,
            //     selectedDate: selectedDate,
            //     classIdType: typeof item.classId
            // })));
            // console.log('Attendance class IDs:', attendanceData.map(item => item.classId));

            setAttendanceData(attendanceData);
            if (selectedClass === 'all') {
                const seen = new Map<string, string>();
                attendanceData.forEach((item: any) => seen.set(String(item.classId), item.className || 'Lớp học'));
                setDayClassOptions([...seen].map(([id, name]) => ({ id, name }))
                    .sort((a, b) => a.name.localeCompare(b.name, 'vi')));
            }
            // Check alerts immediately after setting attendance data, but only if options are loaded
            if (classOptions.length > 0 && campusOptions.length > 0) {
                // console.log('Attendance data loaded, triggering immediate alert check...');
                setTimeout(() => checkMissingAttendanceAlertsWithData(attendanceData), 50);
            }
        } catch (error) {
            console.error('Failed to load attendance data:', error);
            if (!silent) setAttendanceData([]);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const loadClassOptions = async () => {
        try {
            const response = await classesApi.getAll();
            setClassOptions(response.data.data || []);
        } catch (error) {
            console.error('Failed to load classes:', error);
        }
    };

    const loadCampusOptions = async () => {
        try {
            const response = await campusApi.getAll();
            setCampusOptions(response.data.data?.filter((c: any) => c.isActive) || []);
        } catch (error) {
            console.error('Failed to load campuses:', error);
        }
    };

    const loadTeachersAndAssistants = async () => {
        try {
            // console.log('Loading teachers and assistants...');
            const response = await api.get('/users');
            const data = response.data;
            // console.log('Users API response:', data);

            // The API returns {data: Array} directly, not {success: true, data: Array}
            if (data.data && Array.isArray(data.data)) {
                const users = data.data || [];
                // console.log('All users:', users);
                const teachersAndAssistants = users.filter((user: any) =>
                    ['TEACHER', 'ASSISTANT', 'MANAGER', 'ADMIN'].includes(user.role) && user.isActive
                );
                // console.log('Filtered teachers and assistants:', teachersAndAssistants);
                setTeachersAndAssistants(teachersAndAssistants);
            } else {
                console.error('API returned unexpected format:', data);
            }
        } catch (error) {
            console.error('Failed to load teachers and assistants:', error);
        }
    };

    // Check for classes that should have attendance but don't (with data parameter)
    const checkMissingAttendanceAlertsWithData = async (localAttendanceData?: any[]) => {
        // Prevent concurrent executions
        if (isCheckingAlerts) {
            // console.log('🔄 Alert check already in progress, skipping...');
            return;
        }

        setIsCheckingAlerts(true);

        const dataToUse = localAttendanceData || attendanceData;

        // console.log('🔍 === CHECKING MISSING ATTENDANCE ALERTS ===');
        // console.log('📊 Data summary:');
        // console.log('  - classOptions.length:', classOptions.length);
        // console.log('  - campusOptions.length:', campusOptions.length);
        // console.log('  - dataToUse.length:', dataToUse.length);
        // console.log('  - selectedDate:', selectedDate);
        // console.log('  - using localData:', !!localAttendanceData);

        // Don't run if essential data is not loaded yet
        if (!classOptions.length || !campusOptions.length) {
            // console.log('❌ Skipping missing attendance check - options not loaded yet');
            // console.log('  - classOptions.length:', classOptions.length);
            // console.log('  - campusOptions.length:', campusOptions.length);
            setIsCheckingAlerts(false);
            return;
        }

        if (!dataToUse || dataToUse.length === 0) {
            // console.log('⚠️ No attendance data available, will check all classes with schedule');
        }

        try {
            // Get current date info
            const selectedDateObj = parseLocalDate(selectedDate);
            const dayOfWeek = selectedDateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const currentDayName = dayNames[dayOfWeek];

            // Get current time for comparison
            const now = new Date();
            const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes

            // Only check if the selected date is today or in the past
            const isToday = selectedDate === formatDateString(now);
            // Compare only dates, not time - create today's date at 00:00:00 for comparison
            const todayAtMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const isPastDate = selectedDateObj < todayAtMidnight;

            // Debug logging for date comparison
            // console.log('🔍 Date comparison debug:');
            // console.log('  - selectedDate:', selectedDate);
            // console.log('  - selectedDateObj:', selectedDateObj);
            // console.log('  - now:', now);
            // console.log('  - todayAtMidnight:', todayAtMidnight);
            // console.log('  - isToday:', isToday);
            // console.log('  - isPastDate:', isPastDate);

            if (!isToday && !isPastDate) {
                setMissingAttendanceAlerts([]);
                setNoStudentAttendanceAlerts([]);
                setIsCheckingAlerts(false);
                return;
            }

            // Use existing classOptions instead of making another API call
            const allClasses = classOptions;
            // console.log(`📚 Total classes to check: ${allClasses.length}`);
            // console.log('All classes:', allClasses.map(c => ({
            //     id: c.id,
            //     name: c.name,
            //     isActive: c.isActive,
            //     startDate: c.startDate,
            //     endDate: c.endDate
            // })));

            // Filter classes that should have attendance on the selected day
            const classesWithScheduleToday = allClasses.filter((classItem: any) => {
                // console.log(`🔍 Filtering class ${classItem.name}:`);

                // Check if class is active and within date range
                if (!classItem.isActive) {
                    // console.log(`  ❌ Class ${classItem.name} is not active`);
                    return false;
                }

                const classStartDate = new Date(classItem.startDate);
                const classEndDate = classItem.endDate ? new Date(classItem.endDate) : null;

                // Convert to date-only for comparison (ignore time)
                const classStartDateOnly = new Date(classStartDate.getFullYear(), classStartDate.getMonth(), classStartDate.getDate());
                const classEndDateOnly = classEndDate ? new Date(classEndDate.getFullYear(), classEndDate.getMonth(), classEndDate.getDate()) : null;

                // console.log(`  📅 Date check for ${classItem.name}:`);
                // console.log(`    - selectedDateObj: ${selectedDateObj}`);
                // console.log(`    - classStartDate (original): ${classStartDate}`);
                // console.log(`    - classStartDateOnly: ${classStartDateOnly}`);
                // console.log(`    - classEndDate (original): ${classEndDate}`);
                // console.log(`    - classEndDateOnly: ${classEndDateOnly}`);
                // console.log(`    - selectedDateObj < classStartDateOnly: ${selectedDateObj < classStartDateOnly}`);
                // console.log(`    - classEndDateOnly && selectedDateObj > classEndDateOnly: ${classEndDateOnly && selectedDateObj > classEndDateOnly}`);

                if (selectedDateObj < classStartDateOnly) {
                    // console.log(`  ❌ Class ${classItem.name} hasn't started yet`);
                    return false;
                }
                if (classEndDateOnly && selectedDateObj > classEndDateOnly) {
                    // console.log(`  ❌ Class ${classItem.name} has ended`);
                    return false;
                }

                // Check if class has schedule for this day
                try {
                    const schedule = JSON.parse(classItem.schedule || '{}');
                    const hasScheduleToday = !!schedule[currentDayName];
                    // console.log(`  Schedule check for ${classItem.name}:`);
                    // console.log(`    - currentDayName: ${currentDayName}`);
                    // console.log(`    - schedule: ${JSON.stringify(schedule)}`);
                    // console.log(`    - hasScheduleToday: ${hasScheduleToday}`);

                    if (hasScheduleToday) {
                        // console.log(`  ✅ Class ${classItem.name} passed all filters`);
                    } else {
                        // console.log(`  ❌ Class ${classItem.name} has no schedule today`);
                    }

                    return hasScheduleToday;
                } catch (error) {
                    // console.log(`  ❌ Class ${classItem.name} has invalid schedule JSON:`, error);
                    return false;
                }
            });

            // Get existing attendance data to compare - only classes that actually have attendance sessions
            const existingAttendanceIds = dataToUse
                .filter(item => item.id !== null && item.id !== undefined) // Only classes with actual attendance records
                .map(item => String(item.classId));
            // console.log('🔍 Debug missing attendance check:');
            // console.log('Existing attendance IDs (only with actual sessions):', existingAttendanceIds);
            // console.log('All attendance data:', dataToUse.map(item => ({
            //     id: item.id,
            //     classId: item.classId,
            //     className: item.className,
            //     hasAttendance: item.id !== null
            // })));
            // console.log('Classes with schedule today:', classesWithScheduleToday.map(c => ({ id: String(c.id), name: c.name })));

            // Find classes that should have attendance but don't
            const missingAlerts = [];

            for (const classItem of classesWithScheduleToday) {
                // Skip if attendance already exists
                // console.log(`🔍 Checking class ${classItem.name} (ID: ${classItem.id})`);
                // console.log('Existing attendance IDs:', existingAttendanceIds);
                // console.log('Class ID type:', typeof classItem.id, 'Attendance ID types:', existingAttendanceIds.map(id => typeof id));

                const hasAttendance = existingAttendanceIds.includes(String(classItem.id));
                // console.log(`Class ${classItem.name} (ID: ${String(classItem.id)}) has attendance:`, hasAttendance);

                if (hasAttendance) {
                    // console.log(`✅ Skipping class ${classItem.name} - already has attendance`);
                    continue;
                }

                // console.log(`⚠️ Class ${classItem.name} missing attendance - will create alert if time conditions are met`);

                try {
                    const schedule = JSON.parse(classItem.schedule || '{}');
                    const timeSlot = schedule[currentDayName];

                    if (timeSlot && typeof timeSlot === 'string' && timeSlot.includes('-')) {
                        const [startTime] = timeSlot.split('-');
                        const [startHour, startMinute] = startTime.split(':').map(Number);
                        const classStartTime = startHour * 60 + startMinute;

                        // console.log(`  📅 Time check for ${classItem.name}:`);
                        // console.log(`    - timeSlot: ${timeSlot}`);
                        // console.log(`    - startTime: ${startTime}`);
                        // console.log(`    - classStartTime: ${classStartTime} minutes`);
                        // console.log(`    - currentTime: ${currentTime} minutes`);
                        // console.log(`    - isToday: ${isToday}`);
                        // console.log(`    - isPastDate: ${isPastDate}`);
                        // console.log(`    - should alert (isToday && currentTime > classStartTime): ${isToday && currentTime > classStartTime}`);
                        // console.log(`    - should alert (isPastDate): ${isPastDate}`);

                        // Only alert if current time has passed the class start time (for today only)
                        if (isToday && currentTime > classStartTime) {
                            // Find campus info - prioritize classItem.campus over campusOptions lookup
                            let campusName = 'Chưa xác định cơ sở';
                            if (classItem.campus && typeof classItem.campus === 'object' && classItem.campus.name) {
                                campusName = classItem.campus.name;
                            } else if (typeof classItem.campus === 'string') {
                                campusName = classItem.campus;
                            } else {
                                // Fallback to campusOptions lookup
                                const campus = campusOptions.find(c => c.id === classItem.campusId);
                                campusName = campus?.name || 'Chưa xác định cơ sở';
                            }

                            // Handle subject - could be object or string
                            let subjectName = '';
                            if (classItem.subject && typeof classItem.subject === 'object' && classItem.subject.name) {
                                subjectName = classItem.subject.name;
                            } else if (typeof classItem.subject === 'string') {
                                subjectName = classItem.subject;
                            } else {
                                subjectName = 'Chưa xác định môn học';
                            }

                            // console.log(`✅ Adding missing alert for class ${classItem.name}:`);
                            // console.log('- Campus:', campusName, '(from campusId:', classItem.campusId, ')');
                            // console.log('- Subject:', subjectName, '(original:', classItem.subject, ')');

                            missingAlerts.push({
                                id: classItem.id,
                                className: classItem.name,
                                subject: subjectName,
                                campus: campusName,
                                timeSlot: timeSlot,
                                startTime: startTime,
                                room: classItem.room || 'Chưa xác định',
                                teacher: classItem.teacher || 'Chưa phân công',
                                minutesLate: Math.floor((currentTime - classStartTime) / 60) * 60 + (currentTime - classStartTime) % 60
                            });
                        } else if (isPastDate) {
                            // console.log(`  📅 Past date - adding alert for ${classItem.name}`);
                            // For past dates, show all missing attendance
                            let campusName = 'Chưa xác định cơ sở';
                            if (classItem.campus && typeof classItem.campus === 'object' && classItem.campus.name) {
                                campusName = classItem.campus.name;
                            } else if (typeof classItem.campus === 'string') {
                                campusName = classItem.campus;
                            } else {
                                // Fallback to campusOptions lookup
                                const campus = campusOptions.find(c => c.id === classItem.campusId);
                                campusName = campus?.name || 'Chưa xác định cơ sở';
                            }

                            // Handle subject - could be object or string
                            let subjectName = '';
                            if (classItem.subject && typeof classItem.subject === 'object' && classItem.subject.name) {
                                subjectName = classItem.subject.name;
                            } else if (typeof classItem.subject === 'string') {
                                subjectName = classItem.subject;
                            } else {
                                subjectName = 'Chưa xác định môn học';
                            }

                            missingAlerts.push({
                                id: classItem.id,
                                className: classItem.name,
                                subject: subjectName,
                                campus: campusName,
                                timeSlot: timeSlot,
                                startTime: startTime,
                                room: classItem.room || 'Chưa xác định',
                                teacher: classItem.teacher || 'Chưa phân công',
                                isPastDate: true
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error parsing schedule for class:', classItem.name, error);
                }
            }

            // Find classes that have attendance sessions but no student attendance records
            const noStudentAttendanceAlertsArray = [];

            for (const attendanceItem of dataToUse) {
                // Only check classes that have actual attendance sessions (id is not null)
                if (attendanceItem.id !== null && attendanceItem.id !== undefined) {
                    // Check if no students have been marked for attendance
                    const hasStudentAttendance = (attendanceItem.presentCount || 0) +
                        (attendanceItem.absentCount || 0) +
                        (attendanceItem.lateCount || 0) +
                        (attendanceItem.excusedCount || 0) > 0;

                    if (!hasStudentAttendance) {
                        // Find the corresponding class info
                        const classInfo = classesWithScheduleToday.find(c => String(c.id) === String(attendanceItem.classId));

                        if (classInfo) {
                            try {
                                const schedule = JSON.parse(classInfo.schedule || '{}');
                                const timeSlot = schedule[currentDayName];

                                if (timeSlot && typeof timeSlot === 'string' && timeSlot.includes('-')) {
                                    const [startTime] = timeSlot.split('-');
                                    const [startHour, startMinute] = startTime.split(':').map(Number);
                                    const classStartTime = startHour * 60 + startMinute;

                                    // Only alert if current time has passed the class start time (for today only)
                                    const shouldAlert = isPastDate || (isToday && currentTime > classStartTime);

                                    if (shouldAlert) {
                                        // Get campus and subject names
                                        let campusName = 'Chưa xác định cơ sở';
                                        if (classInfo.campus && typeof classInfo.campus === 'object' && classInfo.campus.name) {
                                            campusName = classInfo.campus.name;
                                        } else if (classInfo.campusId) {
                                            const campus = campusOptions.find(c => c.id === classInfo.campusId);
                                            if (campus) {
                                                campusName = campus.name;
                                            }
                                        }

                                        let subjectName = 'Chưa xác định môn học';
                                        if (classInfo.subject && typeof classInfo.subject === 'object' && classInfo.subject.name) {
                                            subjectName = classInfo.subject.name;
                                        } else if (typeof classInfo.subject === 'string') {
                                            subjectName = classInfo.subject;
                                        }

                                        noStudentAttendanceAlertsArray.push({
                                            id: classInfo.id,
                                            className: classInfo.name,
                                            subject: subjectName,
                                            campus: campusName,
                                            timeSlot: timeSlot,
                                            startTime: startTime,
                                            room: classInfo.room || 'Chưa xác định',
                                            teacher: classInfo.teacher || 'Chưa phân công',
                                            minutesLate: isPastDate ? 0 : Math.floor((currentTime - classStartTime) / 60) * 60 + (currentTime - classStartTime) % 60,
                                            isPastDate: isPastDate,
                                            attendanceId: attendanceItem.id // Store attendance session ID for actions
                                        });
                                    }
                                }
                            } catch (error) {
                                console.error('Error parsing schedule for class:', classInfo.name, error);
                            }
                        }
                    }
                }
            }

            // Apply filters if any
            let filteredAlerts = missingAlerts;
            let filteredNoStudentAlerts = noStudentAttendanceAlertsArray;

            if (selectedCampus !== 'all') {
                const selectedCampusName = campusOptions.find(c => c.id === selectedCampus)?.name;
                filteredAlerts = filteredAlerts.filter(alert => alert.campus === selectedCampusName);
                filteredNoStudentAlerts = filteredNoStudentAlerts.filter(alert => alert.campus === selectedCampusName);
            }

            if (selectedClass !== 'all') {
                filteredAlerts = filteredAlerts.filter(alert => alert.id === selectedClass);
                filteredNoStudentAlerts = filteredNoStudentAlerts.filter(alert => alert.id === selectedClass);
            }

            // console.log('🎯 Final results:');
            // console.log('  - Missing attendance alerts:', filteredAlerts.length, filteredAlerts.map(a => a.className));
            // console.log('  - No student attendance alerts:', filteredNoStudentAlerts.length, filteredNoStudentAlerts.map(a => a.className));

            setMissingAttendanceAlerts(filteredAlerts);
            setNoStudentAttendanceAlerts(filteredNoStudentAlerts);

        } catch (error) {
            console.error('Failed to check missing attendance alerts:', error);
            setMissingAttendanceAlerts([]);
            setNoStudentAttendanceAlerts([]);
        } finally {
            setIsCheckingAlerts(false);
        }
    };

    // Wrapper function for backward compatibility
    const checkMissingAttendanceAlerts = async () => {
        await checkMissingAttendanceAlertsWithData();
    };

    const handleViewDetail = async (classItem: any) => {
        if (loadingDetailClassId === classItem.classId) return;

        try {
            setLoadingDetailClassId(classItem.classId);
            setHasUnsavedDetailChanges(false);
            setSessionJustCreated(false); // Reset flag when opening modal
            setSelectedClassDetail(classItem);

            // Load existing attendance data for this class on the selected date
            const attendanceResponse = await attendanceApi.getByClass(classItem.classId, selectedDate);
            const attendanceData = attendanceResponse.data.data;
            setSessionRegistration(attendanceData?.registration ?? null);
            setDetailClassCampusId(attendanceData?.class?.campusId ?? '');

            if (attendanceData && attendanceData.students && attendanceData.attendance) {
                // Use the existing attendance data and load attendance info
                const students = attendanceData.students.map((student: any) => ({
                    id: student.id,
                    studentAttendanceId: student.studentAttendanceId || null,
                    name: student.name,
                    phone: student.phone || '',
                    email: student.email || '',
                    status: student.status || 'not_marked',
                    checkedAt: student.checkedAt ? new Date(student.checkedAt) : null,
                    lateArrivalTime: student.lateArrivalTime ? new Date(student.lateArrivalTime) : null,
                    lateNote: student.lateNote || '',
                    absentNote: student.absentNote || '',
                    excusedNote: student.excusedNote || '',
                    note: student.note || '',
                    isTemporary: Boolean(student.isTemporary),
                    // Thêm thông tin bài tập về nhà
                    homework: student.homework || {
                        assignedExercises: 0.0,
                        completedExercises: 0.0,
                        correctExercises: 0.0,
                        note: ''
                    }
                }));

                setClassStudents(students);

                // Load attendance session info
                setAttendanceSession(attendanceData.attendance);
                setSelectedTeacher(attendanceData.attendance?.teacherId || '');
                setSelectedAssistant(attendanceData.attendance?.assistantId || '');
                setAttendanceNotes(attendanceData.attendance?.notes || '');
            } else if (attendanceData && attendanceData.attendance && !attendanceData.students) {
                // Session exists but students not loaded yet, load students from class
                const studentsResponse = await classesApi.getStudents(classItem.classId);
                const realStudents = studentsResponse.data.data || [];

                const students = realStudents.map((student: any) => ({
                    id: student.id,
                    studentAttendanceId: null,
                    name: student.firstName && student.lastName ?
                        `${student.lastName} ${student.firstName}` :
                        student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
                    phone: student.phone || '',
                    email: student.email || '',
                    status: 'not_marked',
                    checkedAt: null,
                    lateArrivalTime: null,
                    lateNote: '',
                    absentNote: '',
                    excusedNote: '',
                    // Thêm thông tin bài tập về nhà mặc định
                    homework: {
                        assignedExercises: 0,
                        completedExercises: 0,
                        correctExercises: 0,
                        note: ''
                    }
                }));

                setClassStudents(students);

                // Load attendance session info
                setAttendanceSession(attendanceData.attendance);
                setSelectedTeacher(attendanceData.attendance?.teacherId || '');
                setSelectedAssistant(attendanceData.attendance?.assistantId || '');
                setAttendanceNotes(attendanceData.attendance?.notes || '');
            } else if (attendanceData?.students) {
                // No session yet: the detail response already lists the enrolled students
                setClassStudents(attendanceData.students.map((student: any) => ({
                    ...student,
                    status: 'not_marked',
                    checkedAt: null,
                    lateArrivalTime: null,
                    homework: {
                        assignedExercises: 0,
                        completedExercises: 0,
                        correctExercises: 0,
                        note: ''
                    }
                })));
                setAttendanceSession(null);
                setSelectedTeacher('');
                setSelectedAssistant('');
                setAttendanceNotes('');
            } else {
                // Fallback to loading fresh student data if no attendance data exists
                const studentsResponse = await classesApi.getStudents(classItem.classId);
                const realStudents = studentsResponse.data.data || [];

                const students = realStudents.map((student: any) => ({
                    id: student.id,
                    studentAttendanceId: null,
                    name: student.firstName && student.lastName ?
                        `${student.lastName} ${student.firstName}` :
                        student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
                    phone: student.phone || '',
                    email: student.email || '',
                    status: 'not_marked',
                    checkedAt: null,
                    lateArrivalTime: null,
                    lateNote: '',
                    absentNote: '',
                    excusedNote: '',
                    // Thêm thông tin bài tập về nhà mặc định
                    homework: {
                        assignedExercises: 0,
                        completedExercises: 0,
                        correctExercises: 0,
                        note: ''
                    }
                }));

                setClassStudents(students);

                // Reset attendance session for new attendance
                setAttendanceSession(null);
                setSelectedTeacher('');
                setSelectedAssistant('');
                setAttendanceNotes('');
            }

            setDetailTab('attendance');
            setVisitedTabs(['attendance']);
            setShowDetailModal(true);
        } catch (error) {
            console.error('Failed to load class students:', error);
            showToast('❌ Không thể tải danh sách học sinh!', 'error');
        } finally {
            setLoadingDetailClassId(null);
        }
    };

    // Reloads who is registered and their hours without touching unsaved student marks.
    // Sau khi đăng ký/huỷ: cập nhật modal và tải ngầm danh sách, không thay cả trang bằng vòng xoay.
    const refreshSessionStaff = async () => {
        if (!selectedClassDetail) return;
        try {
            const response = await attendanceApi.getByClass(selectedClassDetail.classId, selectedDate);
            const data = response.data.data;
            setSessionRegistration(data?.registration ?? null);
            if (data?.attendance) {
                setAttendanceSession((current: any) => ({ ...(current || {}), ...data.attendance }));
                setSelectedTeacher(data.attendance.teacherId || '');
                setSelectedAssistant(data.attendance.assistantId || '');
            }
            await loadAttendanceData({ silent: true });
        } catch (error) {
            console.error('Failed to refresh session staff:', error);
        }
    };

    // Điểm danh chỉ đổi trên máy; bấm "Lưu điểm danh" mới gửi cả lớp lên server.
    const updateRollCallStudent = (studentId: string, update: (student: any) => any) => {
        setClassStudents(current => current.map(student => (student.id === studentId ? update(student) : student)));
        setHasUnsavedDetailChanges(true);
    };

    const handleMarkStudentAttendance = (studentId: string, status: RollCallStatus) => {
        updateRollCallStudent(studentId, student => {
            if (student.status === status) return student;
            const now = new Date();
            return {
                ...student,
                status,
                checkedAt: now,
                // Đi muộn: mặc định đến lúc vừa bấm, sửa được ngay trên dòng.
                lateArrivalTime: status === 'late' ? (student.lateArrivalTime || now) : student.lateArrivalTime,
            };
        });
    };

    const handleRollCallDetailChange = (studentId: string, detail: RollCallDetail) => {
        updateRollCallStudent(studentId, student => {
            const next = { ...student };
            if (detail.lateTime !== undefined) {
                const [hours, minutes] = detail.lateTime.split(':').map(Number);
                if (Number.isFinite(hours) && Number.isFinite(minutes)) {
                    const arrival = parseLocalDate(selectedDate);
                    arrival.setHours(hours, minutes, 0, 0);
                    next.lateArrivalTime = arrival;
                }
            }
            if (detail.note !== undefined) next.note = detail.note;
            if (detail.lateNote !== undefined) next.lateNote = detail.lateNote;
            if (detail.absentNote !== undefined) next.absentNote = detail.absentNote;
            if (detail.excusedNote !== undefined) next.excusedNote = detail.excusedNote;
            return next;
        });
    };

    // Bỏ học sinh học bù thêm nhầm; lưu điểm danh sẽ xoá bản ghi của em đó trong buổi.
    const handleRemoveMakeupStudent = (studentId: string) => {
        setClassStudents(current => current.filter(student => !(student.id === studentId && student.isTemporary)));
        setHasUnsavedDetailChanges(true);
    };

    const handleMarkAllPresent = () => {
        const now = new Date();
        setClassStudents(current => current.map(student => (
            ['present', 'late', 'absent', 'excused'].includes(student.status)
                ? student
                : { ...student, status: 'present', checkedAt: now }
        )));
        setHasUnsavedDetailChanges(true);
    };

    // Sau khi lưu ở tab BTVN, cập nhật dòng tóm tắt bài tập trong danh sách điểm danh.
    const handleHomeworkSheetSaved = (students: GradeSheetStudent[]) => {
        const byId = new Map(students.map(student => [student.id, student.homework]));
        setClassStudents(current => current.map(student => (byId.has(student.id)
            ? { ...student, homework: byId.get(student.id) ?? undefined }
            : student)));
    };

    const saveAllAttendance = async () => {
        setIsSavingAll(true);
        try {
            // Prepare attendance data for all students
            const selectedDateObj = new Date(selectedDate);
            const studentAttendances = classStudents
                .filter(student => student.status && ['present', 'absent', 'late', 'excused'].includes(student.status))
                .map(student => {
                    // Ensure dates are consistent with selected date
                    const currentTime = new Date();
                    let checkedAt = currentTime;
                    let lateArrivalTime = undefined;

                    // For consistent date handling, use selected date as base
                    if (student.checkedAt) {
                        const checkTime = new Date(student.checkedAt);
                        checkedAt = new Date(selectedDateObj);
                        checkedAt.setHours(checkTime.getHours(), checkTime.getMinutes(), checkTime.getSeconds());
                    }

                    if (student.lateArrivalTime && student.status === 'late') {
                        const lateTime = new Date(student.lateArrivalTime);
                        lateArrivalTime = new Date(selectedDateObj);
                        lateArrivalTime.setHours(lateTime.getHours(), lateTime.getMinutes(), lateTime.getSeconds());
                    }

                    return {
                        studentId: student.id,
                        status: student.status,
                        note: student.note || undefined,
                        checkedAt: checkedAt.toISOString(),
                        lateArrivalTime: lateArrivalTime ? lateArrivalTime.toISOString() : undefined,
                        lateNote: student.status === 'late' && student.lateNote ? student.lateNote : undefined,
                        absentNote: student.status === 'absent' && student.absentNote ? student.absentNote : undefined,
                        excusedNote: student.status === 'excused' && student.excusedNote ? student.excusedNote : undefined,
                        // BTVN lưu ở tab riêng; server giữ nguyên khi lưu lại điểm danh.
                        isTemporary: student.isTemporary || false
                    };
                });

            // Save all attendance data
            const attendanceData = {
                classId: selectedClassDetail.classId,
                date: selectedDate,
                studentAttendances,
                teacherId: selectedTeacher || undefined,
                assistantId: selectedAssistant || undefined,
                notes: attendanceNotes || undefined
            };

            await attendanceApi.markAttendance(attendanceData);

            // Calculate counts for updating local state
            const presentCount = classStudents.filter(s => s.status === 'present').length;
            const absentCount = classStudents.filter(s => s.status === 'absent').length;
            const lateCount = classStudents.filter(s => s.status === 'late').length;
            const excusedCount = classStudents.filter(s => s.status === 'excused').length;

            // Update the main attendance data
            setAttendanceData(prev => prev.map(attendance =>
                attendance.classId === selectedClassDetail.classId
                    ? {
                        ...attendance,
                        students: classStudents, // This already includes lateArrivalTime
                        presentCount,
                        absentCount,
                        lateCount,
                        excusedCount,
                        // Saving marks students; only an ended session with everyone marked is "Hoàn thành".
                        status: attendance.status === 'not_marked'
                            && classStudents.every(student => student.status && student.status !== 'not_marked')
                            ? 'completed'
                            : attendance.status
                    }
                    : attendance
            ));

            showToast('✅ Đã lưu điểm danh thành công!', 'success');
            setHasUnsavedDetailChanges(false);
            setGradesVersion(version => version + 1);
            // The save created the session; load it so the modal shows it as created.
            if (!attendanceSession) await refreshSessionStaff();
        } catch (error) {
            console.error('Failed to save all attendance:', error);
            showToast('❌ Lỗi khi lưu điểm danh. Vui lòng thử lại!', 'error');
        } finally {
            setIsSavingAll(false);
        }
    };

    // Save all attendance function
    const handleSaveAllAttendance = async () => {
        // A class with no students can still save its assignment and notes.
        if (!selectedClassDetail || (classStudents.length === 0 && !hasUnsavedDetailChanges)) {
            showToast('Chưa có thay đổi nào để lưu.', 'info');
            return;
        }

        const unmarkedCount = classStudents.filter(student => !['present', 'absent', 'late', 'excused'].includes(student.status)).length;
        if (unmarkedCount > 0) {
            setConfirmationDialog({
                title: 'Một số học sinh chưa được điểm danh',
                description: `Hiện còn ${unmarkedCount} học sinh chưa có trạng thái điểm danh. Bạn có thể quay lại kiểm tra danh sách hoặc tiếp tục lưu những dữ liệu đã nhập.`,
                confirmLabel: 'Vẫn lưu dữ liệu',
                cancelLabel: 'Kiểm tra lại',
                tone: 'warning',
                onConfirm: saveAllAttendance
            });
            return;
        }

        await saveAllAttendance();
    };

    const closeDetailModal = async () => {
        setShowDetailModal(false);
        setHasUnsavedDetailChanges(false);
        setSessionJustCreated(false); // Reset flag when closing modal
        // Refresh attendance data to show updated counts in the main view
        await loadAttendanceData({ silent: true });
    };

    // Handle modal close with refresh
    const handleCloseDetailModal = async () => {
        if (hasUnsavedDetailChanges) {
            setConfirmationDialog({
                title: 'Bạn có thay đổi chưa lưu',
                description: 'Nếu đóng cửa sổ này, điểm danh, phân công hoặc ghi chú buổi học chưa lưu sẽ không được giữ lại.',
                confirmLabel: 'Đóng và bỏ thay đổi',
                cancelLabel: 'Tiếp tục chỉnh sửa',
                tone: 'warning',
                onConfirm: closeDetailModal
            });
            return;
        }

        await closeDetailModal();
    };

    const handleConfirmDialogAction = async () => {
        if (!confirmationDialog) return;

        setIsConfirmingDialog(true);
        try {
            await confirmationDialog.onConfirm();
            setConfirmationDialog(null);
        } finally {
            setIsConfirmingDialog(false);
        }
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        showUiToast(message, type);
    };

    const attendanceStatusLabels: Record<string, string> = {
        present: 'Có mặt',
        late: 'Đi trễ',
        absent: 'Vắng',
        excused: 'Có phép',
        not_marked: 'Chưa điểm danh',
    };

    const getCurrentClassName = () =>
        selectedClassDetail?.className || selectedClassDetail?.name || 'Lớp học';

    const getStudentAttendanceNotes = (student: any) => [
        student.status === 'late' ? student.lateNote : '',
        student.status === 'absent' ? student.absentNote : '',
        student.status === 'excused' ? student.excusedNote : '',
        student.note,
    ].filter((note) => typeof note === 'string' && note.trim()).map((note) => note.trim());

    const getAttendanceTotalsSnapshot = () => ({
        present: classStudents.filter((student) => student.status === 'present').length,
        absent: classStudents.filter((student) => student.status === 'absent').length,
        late: classStudents.filter((student) => student.status === 'late').length,
        excused: classStudents.filter((student) => student.status === 'excused').length,
        unmarked: classStudents.filter((student) => student.status === 'not_marked').length,
        total: classStudents.length,
    });

    const buildAttendanceClassContext = () => ({
        classId: selectedClassDetail?.classId,
        className: getCurrentClassName(),
        campus: selectedClassDetail?.campus,
        subject: selectedClassDetail?.subject,
        room: selectedClassDetail?.room,
        lessonDate: selectedDate,
        timeSlot: selectedClassDetail?.timeSlot,
        teacher: selectedClassDetail?.teacher,
        assistant: selectedClassDetail?.assistant,
        lessonNote: attendanceNotes,
        totals: getAttendanceTotalsSnapshot(),
    });

    const buildAttendanceStudentContext = (student: any, statusLabel: string, teacherNotes: string[]) => ({
        classId: selectedClassDetail?.classId,
        className: getCurrentClassName(),
        campus: selectedClassDetail?.campus,
        subject: selectedClassDetail?.subject,
        room: selectedClassDetail?.room,
        lessonDate: selectedDate,
        timeSlot: selectedClassDetail?.timeSlot,
        teacher: selectedClassDetail?.teacher,
        assistant: selectedClassDetail?.assistant,
        studentId: student.id,
        studentAttendanceId: student.studentAttendanceId,
        studentName: student.name,
        studentPhone: student.phone,
        attendanceStatus: student.status,
        statusLabel,
        checkedAt: student.checkedAt,
        lateArrivalTime: student.lateArrivalTime,
        teacherNotes,
    });

    const handleCreateStudentManualAlert = async (student: any) => {
        if (!attendanceSession?.id || !student.studentAttendanceId) {
            showToast('❌ Cần lưu điểm danh học sinh trước khi tạo cảnh báo.', 'error');
            return;
        }

        const statusLabel = attendanceStatusLabels[student.status] || 'Chưa điểm danh';
        const title = student.status === 'absent'
            ? `Học sinh vắng: ${student.name}`
            : student.status === 'late'
                ? `Học sinh đi trễ: ${student.name}`
                : `Cảnh báo học sinh: ${student.name}`;
        const teacherNotes = getStudentAttendanceNotes(student);
        const alertKey = `student:${student.id}`;

        setCreatingManualAlertKey(alertKey);
        try {
            await dashboardApi.createManualAlert({
                title,
                description: [
                    `${student.name} - ${statusLabel} trong buổi học ${getCurrentClassName()}.`,
                    teacherNotes.length ? `Ghi chú học sinh: ${teacherNotes.join(' | ')}` : '',
                ].filter(Boolean).join('\n'),
                // Same rule as the alerts: absent is high, late and the rest medium.
                severity: student.status === 'absent' ? 'HIGH' : 'MEDIUM',
                source: 'ATTENDANCE_STUDENT',
                studentId: student.id,
                classId: selectedClassDetail?.classId,
                attendanceId: attendanceSession.id,
                studentAttendanceId: student.studentAttendanceId,
                context: buildAttendanceStudentContext(student, statusLabel, teacherNotes),
            });
            showToast('✅ Đã tạo cảnh báo học sinh.', 'success');
        } catch (error: any) {
            showToast(`❌ ${error?.message || 'Không thể tạo cảnh báo học sinh.'}`, 'error');
        } finally {
            setCreatingManualAlertKey(null);
        }
    };

    const handleCreateClassManualAlert = async () => {
        if (!attendanceSession?.id) {
            showToast('❌ Cần tạo buổi học trước khi thêm cảnh báo.', 'error');
            return;
        }

        setCreatingManualAlertKey('class');
        try {
            await dashboardApi.createManualAlert({
                title: `Vấn đề buổi học: ${getCurrentClassName()}`,
                description: attendanceNotes.trim() || `Buổi học ${getCurrentClassName()} cần được theo dõi.`,
                severity: 'MEDIUM',
                source: 'ATTENDANCE_CLASS',
                classId: selectedClassDetail?.classId,
                attendanceId: attendanceSession.id,
                context: buildAttendanceClassContext(),
            });
            showToast('✅ Đã tạo cảnh báo buổi học.', 'success');
        } catch (error: any) {
            showToast(`❌ ${error?.message || 'Không thể tạo cảnh báo buổi học.'}`, 'error');
        } finally {
            setCreatingManualAlertKey(null);
        }
    };

    const handleAssignmentChange = (type: 'teacher' | 'assistant', userId: string) => {
        if (type === 'teacher') setSelectedTeacher(userId);
        else setSelectedAssistant(userId);
        setHasUnsavedDetailChanges(true);
    };

    // Exports live on "Dữ liệu & báo cáo"; this opens the attendance report for the day's month and filters.
    const openExport = () => {
        const params = new URLSearchParams({ report: 'attendance-detail', from: `${selectedDate.slice(0, 7)}-01`, to: selectedDate });
        if (selectedCampus !== 'all') params.set('campusId', selectedCampus);
        if (selectedClass !== 'all') params.set('classId', selectedClass);
        navigate(`/reports?${params.toString()}`);
    };

    const filteredAttendance = attendanceData.filter(item => {
        const matchesSearch = matchesQuery([item.className, item.subject, item.teacher].filter(Boolean).join(' '), searchTerm);
        return matchesSearch;
    });

    const totalStats = filteredAttendance.reduce((acc, item) => ({
        totalStudents: acc.totalStudents + item.totalStudents,
        totalPresent: acc.totalPresent + item.presentCount,
        totalAbsent: acc.totalAbsent + item.absentCount,
        totalLate: acc.totalLate + item.lateCount,
        totalExcused: acc.totalExcused + item.excusedCount
    }), { totalStudents: 0, totalPresent: 0, totalAbsent: 0, totalLate: 0, totalExcused: 0 });

    // Generate alerts from current data
    const mockAlerts = filteredAttendance.flatMap(attendance =>
        attendance.students
            .filter((student: any) => ['absent', 'late'].includes(student.status))
            .map((student: any) => {
                let lateMessage = '';
                let noteMessage = '';

                // Calculate late time if student is late
                if (student.status === 'late' && student.lateArrivalTime) {
                    const arrivalTime = new Date(student.lateArrivalTime);
                    let lateDetails = `đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`;

                    // Calculate how late they were based on class start time
                    if (attendance.timeSlot) {
                        try {
                            const timeSlotMatch = attendance.timeSlot.match(/(\d{2}:\d{2})/);
                            if (timeSlotMatch) {
                                const [startHour, startMinute] = timeSlotMatch[1].split(':').map(Number);

                                // Create class start time on the same date as the selected attendance date
                                const attendanceDate = new Date(attendance.date);
                                const classStartTime = new Date(attendanceDate);
                                classStartTime.setHours(startHour, startMinute, 0, 0);

                                // Create arrival time on the same date as class
                                const adjustedArrivalTime = new Date(attendanceDate);
                                adjustedArrivalTime.setHours(arrivalTime.getHours(), arrivalTime.getMinutes(), arrivalTime.getSeconds());

                                // Calculate difference: lateArrivalTime - start_time
                                const timeDifference = adjustedArrivalTime.getTime() - classStartTime.getTime();
                                const lateMinutes = Math.floor(timeDifference / (1000 * 60));



                                if (lateMinutes > 5) {
                                    // Student is significantly late
                                    const hours = Math.floor(lateMinutes / 60);
                                    const minutes = lateMinutes % 60;

                                    let lateTimeText = '';
                                    if (hours > 0) {
                                        lateTimeText = `${hours}h${minutes > 0 ? ` ${minutes}p` : ''}`;
                                    } else {
                                        lateTimeText = `${minutes} phút`;
                                    }

                                    lateDetails = `muộn ${lateTimeText} (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                } else if (lateMinutes >= -5 && lateMinutes <= 5) {
                                    // Student arrived on time (within 5 minutes window)
                                    lateDetails = `đến đúng giờ (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                } else {
                                    // Student arrived very early or there's a data issue
                                    const earlyMinutes = Math.abs(lateMinutes);
                                    const hours = Math.floor(earlyMinutes / 60);
                                    const minutes = earlyMinutes % 60;

                                    let earlyTimeText = '';
                                    if (hours > 0) {
                                        earlyTimeText = `${hours}h${minutes > 0 ? ` ${minutes}p` : ''}`;
                                    } else {
                                        earlyTimeText = `${minutes} phút`;
                                    }

                                    lateDetails = `đến sớm ${earlyTimeText} (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                }
                            }
                        } catch (e) {
                            console.error('Error parsing class time:', e);
                        }
                    }

                    lateMessage = ` (${lateDetails})`;
                }

                // Add note information
                if (student.status === 'late' && student.lateNote) {
                    noteMessage = ` - ${student.lateNote}`;
                } else if (student.status === 'absent' && student.absentNote) {
                    noteMessage = ` - ${student.absentNote}`;
                } else if (student.note) {
                    noteMessage = ` - ${student.note}`;
                }

                return {
                    id: `alert-${student.id}`,
                    type: student.status,
                    studentName: student.name || 'Chưa có tên',
                    className: attendance.className,
                    campus: attendance.campus,
                    message: student.status === 'late' ? `Học sinh đi muộn${lateMessage}` : 'Học sinh vắng mặt không phép',
                    note: noteMessage,
                    time: student.checkedAt ? new Date(student.checkedAt).toLocaleString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : 'N/A',
                    severity: student.status === 'absent' ? 'high' : 'medium'
                };
            })
    );

    // Generate missing attendance alerts
    const missingAttendanceAlertsFormatted = missingAttendanceAlerts.map(alert => ({
        id: `missing-${alert.id}`,
        type: 'missing_attendance',
        studentName: '',
        className: alert.className,
        campus: alert.campus,
        message: alert.isPastDate ?
            `Lớp ${alert.className} chưa được tạo điểm danh` :
            `Chưa tạo điểm danh cho lớp ${alert.className} - Đã trễ ${Math.floor(alert.minutesLate / 60)}h${alert.minutesLate % 60}p`,
        note: `${alert.subject} - ${alert.timeSlot} - Phòng: ${alert.room}`,
        time: alert.startTime,
        severity: alert.isPastDate ? 'medium' : (alert.minutesLate > 30 ? 'high' : 'medium'),
        classData: alert
    }));

    // Format no student attendance alerts
    const noStudentAttendanceAlertsFormatted = noStudentAttendanceAlerts.map(alert => ({
        id: `no_student_${alert.id}`,
        type: 'no_student_attendance',
        className: alert.className,
        campus: alert.campus,
        message: alert.isPastDate ?
            `Lớp ${alert.className} chưa điểm danh học sinh` :
            `Lớp ${alert.className} chưa điểm danh học sinh - Đã trễ ${Math.floor(alert.minutesLate / 60)}h${alert.minutesLate % 60}p`,
        note: `${alert.subject} - ${alert.timeSlot} - Phòng: ${alert.room}`,
        time: alert.startTime,
        severity: alert.isPastDate ? 'medium' : (alert.minutesLate > 30 ? 'high' : 'medium'),
        classData: alert
    }));

    // Combine all alerts, prioritizing missing attendance alerts, then no student attendance alerts
    const allAlerts: any[] = [...missingAttendanceAlertsFormatted, ...noStudentAttendanceAlertsFormatted, ...filteredAttendance.flatMap(attendance =>
        attendance.students
            .filter((student: any) => ['absent', 'late'].includes(student.status))
            .map((student: any) => {
                let lateMessage = '';
                let noteMessage = '';

                // Calculate late time if student is late
                if (student.status === 'late' && student.lateArrivalTime) {
                    const arrivalTime = new Date(student.lateArrivalTime);
                    let lateDetails = `đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`;

                    // Calculate how late they were based on class start time
                    if (attendance.timeSlot) {
                        try {
                            const timeSlotMatch = attendance.timeSlot.match(/(\d{2}:\d{2})/);
                            if (timeSlotMatch) {
                                const [startHour, startMinute] = timeSlotMatch[1].split(':').map(Number);

                                // Create class start time on the same date as the selected attendance date
                                const attendanceDate = new Date(attendance.date);
                                const classStartTime = new Date(attendanceDate);
                                classStartTime.setHours(startHour, startMinute, 0, 0);

                                // Create arrival time on the same date as class
                                const adjustedArrivalTime = new Date(attendanceDate);
                                adjustedArrivalTime.setHours(arrivalTime.getHours(), arrivalTime.getMinutes(), arrivalTime.getSeconds());

                                // Calculate difference: lateArrivalTime - start_time
                                const timeDifference = adjustedArrivalTime.getTime() - classStartTime.getTime();
                                const lateMinutes = Math.floor(timeDifference / (1000 * 60));



                                if (lateMinutes > 5) {
                                    // Student is significantly late
                                    const hours = Math.floor(lateMinutes / 60);
                                    const minutes = lateMinutes % 60;

                                    let lateTimeText = '';
                                    if (hours > 0) {
                                        lateTimeText = `${hours}h${minutes > 0 ? ` ${minutes}p` : ''}`;
                                    } else {
                                        lateTimeText = `${minutes} phút`;
                                    }

                                    lateDetails = `muộn ${lateTimeText} (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                } else if (lateMinutes >= -5 && lateMinutes <= 5) {
                                    // Student arrived on time (within 5 minutes window)
                                    lateDetails = `đến đúng giờ (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                } else {
                                    // Student arrived very early or there's a data issue
                                    const earlyMinutes = Math.abs(lateMinutes);
                                    const hours = Math.floor(earlyMinutes / 60);
                                    const minutes = earlyMinutes % 60;

                                    let earlyTimeText = '';
                                    if (hours > 0) {
                                        earlyTimeText = `${hours}h${minutes > 0 ? ` ${minutes}p` : ''}`;
                                    } else {
                                        earlyTimeText = `${minutes} phút`;
                                    }

                                    lateDetails = `đến sớm ${earlyTimeText} (lớp bắt đầu ${classStartTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, đến lúc ${arrivalTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })})`;
                                }
                            }
                        } catch (e) {
                            console.error('Error parsing class time:', e);
                        }
                    }

                    lateMessage = ` (${lateDetails})`;
                }

                // Add note information
                if (student.status === 'late' && student.lateNote) {
                    noteMessage = ` - ${student.lateNote}`;
                } else if (student.status === 'absent' && student.absentNote) {
                    noteMessage = ` - ${student.absentNote}`;
                } else if (student.note) {
                    noteMessage = ` - ${student.note}`;
                }

                return {
                    id: `alert-${student.id}`,
                    type: student.status,
                    studentName: student.name || 'Chưa có tên',
                    className: attendance.className,
                    campus: attendance.campus,
                    message: student.status === 'late' ? `Học sinh đi muộn${lateMessage}` : 'Học sinh vắng mặt không phép',
                    note: noteMessage,
                    time: student.checkedAt ? new Date(student.checkedAt).toLocaleString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : 'N/A',
                    severity: student.status === 'absent' ? 'high' : 'medium'
                };
            })
    )];
    // A session where some students were marked but others were skipped.
    const unmarkedStudentAlerts = filteredAttendance.flatMap(attendance =>
        (attendance.unmarkedStudents || []).map((student: any) => ({
            id: `unmarked-${attendance.classId}-${student.id}`,
            type: 'unmarked',
            studentName: student.name,
            className: attendance.className,
            campus: attendance.campus,
            message: 'Học sinh chưa được điểm danh',
            note: '',
            time: attendance.timeSlot,
            severity: 'medium',
            attendanceItem: attendance,
        }))
    );
    allAlerts.push(...unmarkedStudentAlerts);
    const {
        page: attendancePage,
        pageSize: attendancePageSize,
        pagedItems: pagedAttendance,
        setPage: setAttendancePage,
        setPageSize: setAttendancePageSize,
    } = useLocalPagination(
        filteredAttendance,
        ATTENDANCE_PAGE_SIZE,
        `${selectedDate}|${selectedCampus}|${selectedClass}|${searchTerm}`,
    );
    const {
        page: attendanceAlertsPage,
        pageSize: attendanceAlertsPageSize,
        pagedItems: pagedAttendanceAlerts,
        setPage: setAttendanceAlertsPage,
        setPageSize: setAttendanceAlertsPageSize,
    } = useLocalPagination(
        allAlerts,
        ATTENDANCE_ALERT_LIST_PAGE_SIZE,
        `${selectedDate}|${selectedCampus}|${selectedClass}|${searchTerm}`,
    );

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'present': return 'bg-success-100 text-success-800';
            case 'absent': return 'bg-danger-100 text-danger-800';
            case 'late': return 'bg-warning-100 text-warning-800';
            case 'excused': return 'bg-academy-50 text-academy-800';
            default: return 'bg-ink-100 text-ink-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'present': return <CheckCircle className="w-4 h-4 text-success-600" />;
            case 'absent': return <XCircle className="w-4 h-4 text-danger-600" />;
            case 'late': return <Clock className="w-4 h-4 text-warning-600" />;
            case 'excused': return <Award className="w-4 h-4 text-academy-600" />;
            default: return <AlertTriangle className="w-4 h-4 text-ink-600" />;
        }
    };

    const getClassStatusText = (status: string) => {
        switch (status) {
            case 'completed': return 'Hoàn thành';
            case 'in_progress': return 'Đang diễn ra';
            case 'not_started': return 'Chưa bắt đầu';
            case 'not_marked': return 'Chưa điểm danh';
            default: return 'Không xác định';
        }
    };

    const getClassStatusTone = (status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'info';
            case 'not_started': return 'neutral';
            case 'not_marked': return 'warning';
            default: return 'neutral';
        }
    };

    const getAlertSeverityTone = (severity: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
        switch (severity) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            default: return 'info';
        }
    };

    const openAttendanceAlertModal = (modal: AttendanceAlertModal) => {
        setAttendanceAlertModal(modal);
        setAttendanceAlertModalPage(1);
    };

    const closeAttendanceAlertModal = () => {
        setAttendanceAlertModal(null);
        setAttendanceAlertModalPage(1);
        setAttendanceAlertPageSize(5);
    };

    const openAttendanceAlertDetail = (alert: any) => {
        if (alert.type === 'missing_attendance' && alert.classData) {
            const classItem = {
                classId: alert.classData.id,
                className: alert.classData.className,
                subject: alert.classData.subject,
                campus: alert.classData.campus,
                timeSlot: alert.classData.timeSlot,
                room: alert.classData.room,
                teacher: alert.classData.teacher,
                status: 'not_started',
                totalStudents: 0,
                presentCount: 0,
                absentCount: 0,
                lateCount: 0,
                excusedCount: 0,
                students: []
            };
            handleViewDetail(classItem);
            return;
        }

        if (alert.type === 'no_student_attendance' && alert.classData) {
            const attendanceItem = attendanceData.find(item =>
                String(item.classId) === String(alert.classData.id) &&
                item.id === alert.classData.attendanceId
            );

            if (attendanceItem) {
                handleViewDetail(attendanceItem);
            }
        }
    };

    const renderAttendanceAlertCard = (alert: any) => (
        <div key={alert.id} className="rounded-lg border border-ink-200 bg-white p-4">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0 text-ink-500">
                    {alert.type === 'missing_attendance' ? (
                        <AlertTriangle className="h-5 w-5 text-danger-600" />
                    ) : alert.type === 'no_student_attendance' ? (
                        <Users className="h-5 w-5 text-warning-700" />
                    ) : alert.type === 'absent' ? (
                        <XCircle className="h-5 w-5 text-danger-600" />
                    ) : alert.type === 'unmarked' ? (
                        <UserCheck className="h-5 w-5 text-warning-700" />
                    ) : (
                        <Clock className="h-5 w-5 text-warning-700" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <h4 className="min-w-0 text-sm font-medium leading-5 text-ink-900">{alert.message}</h4>
                        <StatusBadge tone={getAlertSeverityTone(alert.severity)} className="shrink-0">
                            {alert.severity === 'high' ? 'Cao' : alert.severity === 'medium' ? 'Trung bình' : 'Thấp'}
                        </StatusBadge>
                    </div>
                    {alert.type === 'missing_attendance' || alert.type === 'no_student_attendance' ? (
                        <p className="mt-1 text-sm text-ink-600">
                            <span className="font-medium text-ink-800">{alert.className}</span> - {alert.campus}
                            {alert.note && <span className="mt-1 block text-xs text-ink-500">{alert.note}</span>}
                        </p>
                    ) : (
                        <p className="mt-1 text-sm text-ink-600">
                            <span className="font-medium text-ink-800">{alert.studentName}</span> - {alert.className}
                            {alert.note && <span className="text-academy-700">{alert.note}</span>}
                        </p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                        <span>{alert.campus}</span>
                        <span>{alert.time}</span>
                        {(alert.type === 'missing_attendance' || alert.type === 'no_student_attendance') && alert.classData ? (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openAttendanceAlertDetail(alert)}
                            >
                                {alert.type === 'missing_attendance' ? 'Tạo điểm danh' : 'Điểm danh'}
                            </Button>
                        ) : alert.type === 'unmarked' ? (
                            <Button variant="secondary" size="sm" onClick={() => handleViewDetail(alert.attendanceItem)}>
                                Điểm danh
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );

    const attendanceAlertModalAlerts = attendanceAlertModal
        ? allAlerts.filter((alert) => {
            if (attendanceAlertModal === 'all') return true;
            if (attendanceAlertModal === 'student_status') return ['absent', 'late', 'unmarked'].includes(alert.type);
            return alert.type === attendanceAlertModal;
        })
        : [];
    const attendanceAlertPageCount = Math.ceil(attendanceAlertModalAlerts.length / attendanceAlertPageSize);
    const pagedAttendanceAlertModalAlerts = attendanceAlertModalAlerts.slice(
        (attendanceAlertModalPage - 1) * attendanceAlertPageSize,
        attendanceAlertModalPage * attendanceAlertPageSize,
    );

    useEffect(() => {
        if (!attendanceAlertModal) return;
        const lastPage = Math.max(1, attendanceAlertPageCount);
        if (attendanceAlertModalPage > lastPage) {
            setAttendanceAlertModalPage(lastPage);
        }
    }, [attendanceAlertModal, attendanceAlertModalPage, attendanceAlertPageCount]);

    // Admins don't want teachers and assistants to see head counts summed across classes.
    const showTotals = user?.role !== 'TEACHER' && user?.role !== 'ASSISTANT';

    const attendanceRate = totalStats.totalStudents > 0
        ? ((totalStats.totalPresent / totalStats.totalStudents) * 100).toFixed(1)
        : 0;

    if (loading) {
        return (
            <div className="space-y-6">
                <PageHeader
                    title="Buổi học"
                    subtitle="Điểm danh, bài tập về nhà và điểm kiểm tra của từng lớp trong ngày."
                />
                <SectionPanel>
                    <LoadingState title="Đang tải dữ liệu điểm danh..." />
                </SectionPanel>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Buổi học"
                subtitle="Điểm danh, bài tập về nhà và điểm kiểm tra của từng lớp trong ngày."
                actions={(
                    <>
                        <Button
                            variant="secondary"
                            icon={<Download className="h-4 w-4" />}
                            onClick={openExport}
                        >
                            Xuất dữ liệu
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            icon={<RefreshCw className="h-4 w-4" />}
                            onClick={() => {
                                loadAttendanceData();
                                showToast('Đã cập nhật dữ liệu!', 'success');
                            }}
                            aria-label="Cập nhật dữ liệu điểm danh"
                            title="Cập nhật"
                        />
                    </>
                )}
            />

            {/* Stats Cards */}
            {showTotals && (
                <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        label="Tổng học sinh"
                        value={totalStats.totalStudents}
                        detail={`${filteredAttendance.length} lớp`}
                        icon={<Users className="h-5 w-5" />}
                        tone="academy"
                    />
                    <StatCard
                        label="Có mặt"
                        value={totalStats.totalPresent}
                        detail={`${attendanceRate}% điểm danh có mặt`}
                        icon={<UserCheck className="h-5 w-5" />}
                        tone="action"
                    />
                    <StatCard
                        label="Vắng"
                        // Absent and excused are separate statuses; the card counts both and splits them in the detail.
                        value={totalStats.totalAbsent + totalStats.totalExcused}
                        detail={`${totalStats.totalAbsent} không phép · ${totalStats.totalExcused} có phép`}
                        icon={<XCircle className="h-5 w-5" />}
                        tone="danger"
                    />
                    <StatCard
                        label="Đi muộn"
                        value={totalStats.totalLate}
                        detail={`${totalStats.totalStudents > 0 ? ((totalStats.totalLate / totalStats.totalStudents) * 100).toFixed(1) : 0}% tổng học sinh`}
                        icon={<Clock className="h-5 w-5" />}
                        tone="warning"
                    />
                </div>
            )}

            {/* Filters */}
            <div data-attendance-date-toolbar className="relative z-30">
                <DataToolbar
                    className="sm:items-center"
                    controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
                >
                    <DateInput aria-label="Ngày" className="lg:w-40" value={selectedDate} onChange={commitSelectedDate} clearable={false} />
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Tìm lớp, giáo viên…"
                        leftIcon={<Search className="h-4 w-4" />}
                        aria-label="Tìm kiếm lớp học, giáo viên"
                        className="min-h-9 lg:w-64"
                    />
                    <select
                        value={selectedCampus}
                        onChange={(e) => {
                            setSelectedCampus(e.target.value);
                            setSelectedClass('all');
                        }}
                        aria-label="Lọc theo cơ sở"
                        className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                    >
                        <option value="all">Tất cả cơ sở</option>
                        {campusOptions.map(campus => (
                            <option key={campus.id} value={campus.id}>{campus.name}</option>
                        ))}
                    </select>
                    <select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        aria-label="Lọc theo lớp học"
                        className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                    >
                        <option value="all">Tất cả lớp</option>
                        {dayClassOptions.map(item => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                    </select>
                </DataToolbar>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <SectionPanel className="overflow-hidden max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                        <div className="border-b border-ink-200 px-4 py-4 sm:px-5">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-base font-semibold text-ink-900">
                                        Điểm danh ngày {parseLocalDate(selectedDate).toLocaleDateString('vi-VN')}
                                    </h2>
                                                                    </div>
                                <span className="text-sm text-ink-500">{filteredAttendance.length} lớp</span>
                            </div>
                        </div>

                        {filteredAttendance.length === 0 ? (
                            <EmptyState
                                className="min-h-64"
                                title="Không có lớp học nào"
                                description={user?.role === 'TEACHER' || user?.role === 'ASSISTANT'
                                    ? `Ngày ${parseLocalDate(selectedDate).toLocaleDateString('vi-VN')} bạn không có lớp nào. Bạn thấy các lớp được phân công và các buổi bạn đã nhận; riêng hôm nay thêm các lớp ở cơ sở bạn điểm danh gần nhất.`
                                    : `Không có lớp học nào có lịch học vào ngày ${parseLocalDate(selectedDate).toLocaleDateString('vi-VN')}. Hãy kiểm tra lại thời gian và lịch học của các lớp.`}
                            />
                        ) : (
                            <>
                            <ResponsiveDataView
                                desktopLabel="Danh sách điểm danh lớp học"
                                mobileLabel="Thẻ điểm danh lớp học"
                                desktop={
                                    <div className="min-w-[760px] divide-y divide-ink-200">
                                        {pagedAttendance.map((item) => (
                                            <div key={item.classId} onClick={openOnRowClick(() => { if (loadingDetailClassId !== item.classId) handleViewDetail(item); })} className="cursor-pointer px-5 py-4 transition-colors duration-200 hover:bg-ink-50">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex min-w-0 items-start gap-4">
                                                        <div className="min-w-0">
                                                            <h3 className="truncate text-base font-semibold text-ink-900">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleViewDetail(item)}
                                                                    disabled={loadingDetailClassId === item.classId}
                                                                    aria-label={`Xem chi tiết lớp ${item.className}`}
                                                                    className="inline-flex cursor-pointer items-center gap-2 text-left hover:text-academy-600 disabled:cursor-wait"
                                                                >
                                                                    {item.className}
                                                                    {loadingDetailClassId === item.classId ? <RefreshCw className="h-4 w-4 animate-spin text-ink-400" /> : null}
                                                                </button>
                                                            </h3>
                                                            <p className="text-sm text-ink-500">{item.subject}</p>
                                                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-600">
                                                                <span className="inline-flex items-center gap-1">
                                                                    <Building className="h-4 w-4 text-ink-400" />
                                                                    {item.campus}
                                                                </span>
                                                                {item.room && (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <MapPin className="h-4 w-4 text-ink-400" />
                                                                        {item.room}
                                                                    </span>
                                                                )}
                                                                <span className="inline-flex items-center gap-1">
                                                                    <Clock className="h-4 w-4 text-ink-400" />
                                                                    {item.timeSlot}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-2">
                                                        <StatusBadge tone={getClassStatusTone(item.status)}>
                                                            {getClassStatusText(item.status)}
                                                        </StatusBadge>
                                                    </div>
                                                </div>

                                                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                                    <div className="flex items-baseline gap-1.5"><dt className="text-ink-500">Có mặt</dt><dd className="font-semibold tabular-nums text-success-700">{item.presentCount}</dd></div>
                                                    <div className="flex items-baseline gap-1.5"><dt className="text-ink-500">Vắng</dt><dd className="font-semibold tabular-nums text-danger-700">{item.absentCount}</dd></div>
                                                    <div className="flex items-baseline gap-1.5"><dt className="text-ink-500">Đi muộn</dt><dd className="font-semibold tabular-nums text-warning-700">{item.lateCount}</dd></div>
                                                    <div className="flex items-baseline gap-1.5"><dt className="text-ink-500">Có phép</dt><dd className="font-semibold tabular-nums text-ink-900">{item.excusedCount}</dd></div>
                                                </dl>

                                                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm text-ink-600">
                                                    <div className="min-w-0">
                                                        <span>Giáo viên: <span className="font-medium text-ink-800">{item.teacher || 'Chưa đăng ký'}</span></span>
                                                        <span className="ml-4">Trợ giảng: <span className="font-medium text-ink-800">{item.assistant || 'Chưa đăng ký'}</span></span>
                                                    </div>
                                                    <span className="text-ink-500">Tổng: {item.totalStudents} học sinh{item.makeupCount ? ` (gồm ${item.makeupCount} học bù)` : ''}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                }
                                mobile={
                                    <>
                                        {pagedAttendance.map((item) => (
                                            <article key={item.classId} onClick={openOnRowClick(() => { if (loadingDetailClassId !== item.classId) handleViewDetail(item); })} className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4 transition-colors hover:bg-ink-50">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <h3 className="truncate text-base font-semibold text-ink-900">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewDetail(item)}
                                                                disabled={loadingDetailClassId === item.classId}
                                                                aria-label={`Xem chi tiết lớp ${item.className}`}
                                                                className="inline-flex max-w-full cursor-pointer items-center gap-2 truncate text-left disabled:cursor-wait"
                                                            >
                                                                {item.className}
                                                                {loadingDetailClassId === item.classId ? <RefreshCw className="h-4 w-4 animate-spin text-ink-400" /> : null}
                                                            </button>
                                                        </h3>
                                                        <p className="truncate text-sm text-ink-500">{item.subject}</p>
                                                    </div>
                                                    <StatusBadge tone={getClassStatusTone(item.status)} className="shrink-0">
                                                        {getClassStatusText(item.status)}
                                                    </StatusBadge>
                                                </div>

                                                <div className="mt-3 space-y-1 text-sm text-ink-600">
                                                    <p className="flex min-w-0 items-center gap-2">
                                                        <Building className="h-4 w-4 shrink-0 text-ink-400" />
                                                        <span className="truncate">{item.campus}</span>
                                                    </p>
                                                    <p className="flex min-w-0 items-center gap-2">
                                                        <Clock className="h-4 w-4 shrink-0 text-ink-400" />
                                                        <span className="truncate">{item.timeSlot}</span>
                                                    </p>
                                                    {item.room && (
                                                        <p className="flex min-w-0 items-center gap-2">
                                                            <MapPin className="h-4 w-4 shrink-0 text-ink-400" />
                                                            <span className="truncate">{item.room}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                                                    <div>
                                                        <p className="text-base font-semibold text-action-700">{item.presentCount}</p>
                                                        <p className="text-[11px] text-ink-500">Có mặt</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-base font-semibold text-danger-700">{item.absentCount}</p>
                                                        <p className="text-[11px] text-ink-500">Vắng</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-base font-semibold text-warning-700">{item.lateCount}</p>
                                                        <p className="text-[11px] text-ink-500">Muộn</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-base font-semibold text-ink-900">{item.excusedCount}</p>
                                                        <p className="text-[11px] text-ink-500">Có phép</p>
                                                    </div>
                                                </div>

                                                <div className="mt-4 flex items-end justify-between gap-3 border-t border-ink-200 pt-3">
                                                    <div className="min-w-0 text-xs text-ink-500">
                                                        <p className="truncate">GV: <span className="font-medium text-ink-700">{item.teacher || 'Chưa đăng ký'}</span></p>
                                                        <p className="truncate">TG: <span className="font-medium text-ink-700">{item.assistant || 'Chưa đăng ký'}</span></p>
                                                    </div>
                                                    <p className="shrink-0 text-right text-xs text-ink-500">
                                                        <span className="block text-base font-semibold tabular-nums text-ink-900">{item.totalStudents}</span>
                                                        học sinh
                                                    </p>
                                                </div>
                                            </article>
                                        ))}
                                    </>
                                }
                            />
                            <LocalPagination
                                label="danh sách điểm danh lớp học"
                                page={attendancePage}
                                pageSize={attendancePageSize}
                                totalItems={filteredAttendance.length}
                                pageSizeOptions={[5, 10, 15, 20]}
                                onPageChange={setAttendancePage}
                                onPageSizeChange={setAttendancePageSize}
                            />
                            </>
                        )}
                    </SectionPanel>
                </div>

                <SectionPanel className="overflow-hidden max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                    <div className="border-b border-ink-200 px-4 py-4 sm:px-5">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="flex items-center text-base font-semibold text-ink-900">
                                <Bell className="mr-2 h-5 w-5 text-danger-600" />
                                Cảnh báo
                            </h2>
                            <StatusBadge tone={allAlerts.length > 0 ? 'danger' : 'success'}>
                                {allAlerts.length}
                            </StatusBadge>
                        </div>
                    </div>

                    <div className="p-4 sm:p-5">
                        {allAlerts.length === 0 ? (
                            <EmptyState className="min-h-40" title="Không có cảnh báo nào hôm nay" />
                        ) : (
                            <div className="space-y-3">
                                <div className="space-y-3">
                                    {pagedAttendanceAlerts.map((alert) => renderAttendanceAlertCard(alert))}
                                </div>
                                <LocalPagination
                                    label="danh sách cảnh báo điểm danh"
                                    page={attendanceAlertsPage}
                                    pageSize={attendanceAlertsPageSize}
                                    totalItems={allAlerts.length}
                                    pageSizeOptions={[5, 10, 15, 20]}
                                    onPageChange={setAttendanceAlertsPage}
                                    onPageSizeChange={setAttendanceAlertsPageSize}
                                    className="-mx-4 px-4 sm:-mx-5 sm:px-5"
                                />
                            </div>
                        )}
                    </div>
                </SectionPanel>
            </div>

            <Modal
                open={Boolean(attendanceAlertModal)}
                title="Danh sách cảnh báo điểm danh"
                description={attendanceAlertModal ? attendanceAlertModalLabels[attendanceAlertModal] : undefined}
                onClose={closeAttendanceAlertModal}
                className="max-w-4xl"
            >
                <div className="mb-4">
                    <DataToolbar className="shadow-none" controlsLayoutClassName="grid flex-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-ink-700">Loại cảnh báo</label>
                            <select
                                value={attendanceAlertModal ?? 'all'}
                                onChange={(event) => {
                                    setAttendanceAlertModal(event.target.value as AttendanceAlertModal);
                                    setAttendanceAlertModalPage(1);
                                }}
                                className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                            >
                                {Object.entries(attendanceAlertModalLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-ink-700">Số lượng</label>
                            <select
                                value={String(attendanceAlertPageSize)}
                                onChange={(event) => {
                                    setAttendanceAlertPageSize(Number(event.target.value));
                                    setAttendanceAlertModalPage(1);
                                }}
                                className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                            >
                                {attendanceAlertPageSizeOptions.map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>{pageSize}</option>
                                ))}
                            </select>
                        </div>
                    </DataToolbar>
                </div>

                {attendanceAlertModalAlerts.length === 0 ? (
                    <EmptyState
                        title="Không có cảnh báo điểm danh"
                        description="Danh sách sẽ tự cập nhật theo dữ liệu điểm danh trong ngày."
                    />
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-3">
                            {pagedAttendanceAlertModalAlerts.map((alert) => renderAttendanceAlertCard(alert))}
                        </div>
                        <div className="flex flex-col gap-3 border-t border-ink-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-ink-500">
                                Trang {attendanceAlertPageCount === 0 ? 0 : attendanceAlertModalPage}/{attendanceAlertPageCount}
                            </p>
                            {attendanceAlertPageCount > 1 ? (
                                <div className="flex gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        aria-label="Trang trước danh sách cảnh báo điểm danh"
                                        disabled={attendanceAlertModalPage <= 1}
                                        onClick={() => setAttendanceAlertModalPage((current) => Math.max(1, current - 1))}
                                    >
                                        Trước
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        aria-label="Trang sau danh sách cảnh báo điểm danh"
                                        disabled={attendanceAlertModalPage >= attendanceAlertPageCount}
                                        onClick={() => setAttendanceAlertModalPage((current) => Math.min(attendanceAlertPageCount, current + 1))}
                                    >
                                        Sau
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Detail Modal */}
            {showDetailModal && selectedClassDetail && (
                <Modal
                    open={showDetailModal}
                    title={`Buổi học lớp ${selectedClassDetail.className}`}
                    description={`${selectedClassDetail.subject} • ${selectedClassDetail.campus} • ${selectedClassDetail.timeSlot}`}
                    onClose={handleCloseDetailModal}
                    className="max-w-5xl sm:max-w-5xl lg:max-w-5xl"
                >
                    <div className="space-y-6">
                        <div>
                                    {/* Permission indicator */}
                                    {attendanceSession && canEditAttendanceData() && (
                                        <div className="mt-2">
                                            {user?.role === 'ADMIN' || user?.role === 'MANAGER' ? (
                                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-success-100 text-success-800 rounded">
                                                    Bạn có toàn quyền quản lý buổi học này
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-academy-50 text-academy-800 rounded">
                                                    Bạn có quyền chỉnh sửa điểm danh cho buổi học này
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {attendanceSession && !canEditAttendanceData() && (
                                        <div className="mt-2">
                                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-ink-100 text-ink-600 rounded">
                                                Bạn chỉ có thể xem thông tin buổi học này
                                            </span>
                                        </div>
                                    )}
                        </div>

                        <div className="-mt-2 flex gap-1 overflow-x-auto border-b border-ink-200" role="tablist" aria-label="Nội dung buổi học">
                            {SESSION_TABS.map(tab => (
                                <button
                                    key={tab.value}
                                    type="button"
                                    role="tab"
                                    aria-selected={detailTab === tab.value}
                                    aria-label={tab.label}
                                    onClick={() => openDetailTab(tab.value)}
                                    className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors ${detailTab === tab.value
                                        ? 'border-academy-600 font-semibold text-ink-900'
                                        : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                                >
                                    <span className="sm:hidden">{tab.short}</span>
                                    <span className="hidden sm:inline">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Mở tab nào thì giữ tab đó, để chuyển qua lại không mất số đang nhập. */}
                        {attendanceSession?.id ? (
                            <>
                                {visitedTabs.includes('homework') ? (
                                    <div hidden={detailTab !== 'homework'}>
                                        <HomeworkSheet
                                            key={`homework-${attendanceSession.id}`}
                                            attendanceId={attendanceSession.id}
                                            refreshKey={gradesVersion}
                                            canEdit={Boolean(canEditAttendanceData())}
                                            hasUnsavedAttendance={hasUnsavedDetailChanges}
                                            onSaved={handleHomeworkSheetSaved}
                                        />
                                    </div>
                                ) : null}
                                {visitedTabs.includes('tests') ? (
                                    <div hidden={detailTab !== 'tests'}>
                                        <AssessmentSheet
                                            key={`tests-${attendanceSession.id}`}
                                            attendanceId={attendanceSession.id}
                                            refreshKey={gradesVersion}
                                            canEdit={Boolean(canEditAttendanceData())}
                                            hasUnsavedAttendance={hasUnsavedDetailChanges}
                                        />
                                    </div>
                                ) : null}
                            </>
                        ) : detailTab !== 'attendance' ? (
                            <EmptyState
                                title="Chưa mở buổi học"
                                description="Nhận buổi và điểm danh ở tab Điểm danh trước, sau đó quay lại đây để nhập."
                            />
                        ) : null}

                        <div hidden={detailTab !== 'attendance'} className="space-y-6">
                            <section className="space-y-4 rounded-lg border border-ink-200 p-4" aria-label="Thông tin buổi học">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {canModifyAttendanceAssignments() ? (
                                <SearchSelect
                                    label="Giáo viên"
                                    value={selectedTeacher}
                                    onChange={value => handleAssignmentChange('teacher', value)}
                                    options={assignmentUserOptions}
                                    placeholder="Nhập tên giáo viên..."
                                    noResultsText="Không tìm thấy giáo viên"
                                    clearable
                                />
                                ) : (
                                    <SessionSlotCard
                                        slot="teacher"
                                        session={attendanceSession}
                                        currentUserId={user?.id || ''}
                                        registration={sessionRegistration}
                                        classInfo={{ id: selectedClassDetail.classId, name: selectedClassDetail.className, campusId: detailClassCampusId }}
                                        onChanged={refreshSessionStaff}
                                    />
                                )}

                                {canModifyAttendanceAssignments() ? (
                                <SearchSelect
                                    label="Trợ giảng"
                                    value={selectedAssistant}
                                    onChange={value => handleAssignmentChange('assistant', value)}
                                    options={assignmentUserOptions}
                                    placeholder="Nhập tên trợ giảng..."
                                    noResultsText="Không tìm thấy trợ giảng"
                                    clearable
                                />
                                ) : (
                                    <SessionSlotCard
                                        slot="assistant"
                                        session={attendanceSession}
                                        currentUserId={user?.id || ''}
                                        registration={sessionRegistration}
                                        classInfo={{ id: selectedClassDetail.classId, name: selectedClassDetail.className, campusId: detailClassCampusId }}
                                        onChanged={refreshSessionStaff}
                                    />
                                )}

                                <div>
                                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                        <label className="block text-sm font-medium text-ink-700">
                                            Ghi chú buổi học
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleCreateClassManualAlert}
                                            disabled={!attendanceSession || creatingManualAlertKey === 'class'}
                                            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${(!attendanceSession || creatingManualAlertKey === 'class')
                                                ? 'cursor-not-allowed bg-ink-100 text-ink-400'
                                                : 'bg-warning-50 text-warning-700 hover:bg-warning-100'
                                            }`}
                                            aria-label="Thêm cảnh báo buổi học"
                                        >
                                            <Bell className="h-3.5 w-3.5" />
                                            <span>{creatingManualAlertKey === 'class' ? 'Đang thêm...' : 'Thêm cảnh báo'}</span>
                                        </button>
                                    </div>
                                    <textarea
                                        value={attendanceNotes}
                                        onChange={(e) => {
                                            setAttendanceNotes(e.target.value);
                                            setHasUnsavedDetailChanges(true);
                                        }}
                                        disabled={!attendanceSession || !canEditAttendanceData()}
                                        placeholder="Ghi chú chung cho buổi học..."
                                        className={`w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500 resize-none ${(!attendanceSession || !canEditAttendanceData()) ? 'bg-ink-100 cursor-not-allowed' : ''}`}
                                        rows={2}
                                    />
                                </div>
                            </div>

                            <SessionStaffTimes
                                session={attendanceSession}
                                currentUserId={user?.id || ''}
                                isManager={Boolean(canModifyAttendanceAssignments())}
                                defaultTimes={sessionRegistration?.defaultTimes ?? { startTime: null, endTime: null }}
                                onChanged={refreshSessionStaff}
                            />
                            </section>

                            {(!attendanceSession && !sessionJustCreated) ? (
                                // Buổi học được mở khi có người nhận buổi (hoặc quản lý xếp người rồi Lưu), không có nút tạo riêng.
                                <div className="rounded-lg border border-warning-200 bg-warning-50 p-4">
                                    <h4 className="text-md font-medium text-warning-800">Chưa mở buổi học</h4>
                                    <p className="mt-1 text-sm text-warning-600">
                                        {canModifyAttendanceAssignments()
                                            ? 'Chọn giáo viên/trợ giảng ở trên rồi bấm Lưu để mở buổi học, hoặc để giáo viên tự nhận buổi.'
                                            : 'Bấm "Nhận buổi" ở phần Giáo viên/Trợ giảng phía trên để mở buổi học và điểm danh.'}
                                    </p>
                                </div>
                            ) : null}

                            <RollCallTable
                                students={classStudents}
                                canEdit={Boolean(attendanceSession && canEditAttendanceData())}
                                dirty={hasUnsavedDetailChanges}
                                saving={isSavingAll}
                                saveDisabled={markingAttendance || isSavingAll || (classStudents.length === 0 && !hasUnsavedDetailChanges) || (attendanceSession
                                    ? !canEditAttendanceData()
                                    // Admin/manager can assign staff before the session exists; saving creates it.
                                    : !(canModifyAttendanceAssignments() && hasUnsavedDetailChanges))}
                                onSave={handleSaveAllAttendance}
                                onStatusChange={handleMarkStudentAttendance}
                                onDetailChange={handleRollCallDetailChange}
                                onMarkAllPresent={handleMarkAllPresent}
                                onRemoveMakeupStudent={handleRemoveMakeupStudent}
                                onAddAlert={student => void handleCreateStudentManualAlert(student)}
                                alertBusyKey={creatingManualAlertKey}
                                alertDisabled={!attendanceSession}
                                onAddMakeupStudent={attendanceSession && canEditAttendanceData() ? () => setShowAddStudentModal(true) : undefined}
                            />
                        </div>
                    </div>
                </Modal>
            )}

            <Modal
                open={Boolean(confirmationDialog)}
                title={confirmationDialog?.title || ''}
                onClose={() => {
                    if (!isConfirmingDialog) {
                        setConfirmationDialog(null);
                    }
                }}
                footer={(
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setConfirmationDialog(null)}
                            disabled={isConfirmingDialog}
                        >
                            {confirmationDialog?.cancelLabel || 'Hủy'}
                        </Button>
                        <Button
                            variant={confirmationDialog?.tone === 'danger' ? 'danger' : 'primary'}
                            onClick={handleConfirmDialogAction}
                            loading={isConfirmingDialog}
                        >
                            {confirmationDialog?.confirmLabel || 'Xác nhận'}
                        </Button>
                    </div>
                )}
            >
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-600">
                        <AlertTriangle className="h-5 w-5" />
                    </div>
                    <p className="text-sm leading-6 text-ink-600">
                        {confirmationDialog?.description}
                    </p>
                </div>
            </Modal>

            {/* Add Student Modal */}
            {showAddStudentModal && (
                <Modal
                    open={showAddStudentModal}
                    title="Thêm học sinh (học bù)"
                    description="Tìm học sinh của lớp khác để ghi nhận học bù buổi này."
                    onClose={closeAddStudentModal}
                    className="max-w-2xl sm:max-w-2xl lg:max-w-2xl"
                    footer={(
                        <div className="flex justify-end gap-3">
                            <Button variant="secondary" onClick={closeAddStudentModal}>Đóng</Button>
                        </div>
                    )}
                >
                    <div className="space-y-4">
                        <Input
                            label="Tìm học sinh"
                            placeholder="Nhập tên học sinh..."
                            leftIcon={<Search className="h-4 w-4" />}
                            value={studentSearchTerm}
                            onChange={(event) => setStudentSearchTerm(event.target.value)}
                            autoFocus
                        />
                        <div className="max-h-96 overflow-y-auto">
                            {makeupQueryTooShort ? (
                                <p className="py-8 text-center text-sm text-ink-500">Nhập ít nhất 2 chữ cái trong tên học sinh.</p>
                            ) : searchingMakeup && !makeupResults.length ? (
                                <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-600">
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                    Đang tìm học sinh...
                                </div>
                            ) : !makeupResults.length ? (
                                <EmptyState title="Không tìm thấy học sinh" description="Thử gõ tên khác, hoặc học sinh đã có trong lớp này." className="min-h-32" />
                            ) : (
                                <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                                    {makeupResults.map((student) => {
                                        const isAlreadyAdded = classStudents.some(s => s.id === student.id);
                                        return (
                                            <li key={student.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                                <div className="min-w-0">
                                                    <p className="font-medium text-ink-900">{student.name}</p>
                                                    <p className="text-xs text-ink-500">
                                                        {student.classes.length
                                                            ? student.classes.map(item => (item.campusName ? `${item.name} (${item.campusName})` : item.name)).join(', ')
                                                            : 'Chưa vào lớp nào'}
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant={isAlreadyAdded ? 'secondary' : 'primary'}
                                                    disabled={isAlreadyAdded}
                                                    onClick={() => handleAddStudent(student)}
                                                >
                                                    {isAlreadyAdded ? 'Đã có trong buổi' : 'Thêm vào buổi'}
                                                </Button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Attendance;
